import { useEffect, useRef, useState, useCallback } from "react";

const COLS = 10;
const ROWS = 20;
const BLOCK = 30;
const NEXT_BLOCK = 24;

const COLORS: Record<string, string> = {
  I: "#22d3ee",
  O: "#facc15",
  T: "#e879f9",
  S: "#4ade80",
  Z: "#f87171",
  J: "#818cf8",
  L: "#fb923c",
};

const PIECES: Record<string, number[][]> = {
  I: [[1, 1, 1, 1]],
  O: [[1, 1], [1, 1]],
  T: [[0, 1, 0], [1, 1, 1]],
  S: [[0, 1, 1], [1, 1, 0]],
  Z: [[1, 1, 0], [0, 1, 1]],
  J: [[1, 0, 0], [1, 1, 1]],
  L: [[0, 0, 1], [1, 1, 1]],
};

const PIECE_KEYS = Object.keys(PIECES);

type Board = (string | null)[][];
type PieceState = { type: string; shape: number[][]; x: number; y: number };

function emptyBoard(): Board {
  return Array.from({ length: ROWS }, () => Array(COLS).fill(null));
}

function randomPiece(): PieceState {
  const type = PIECE_KEYS[Math.floor(Math.random() * PIECE_KEYS.length)];
  const shape = PIECES[type];
  return { type, shape, x: Math.floor(COLS / 2) - Math.floor(shape[0].length / 2), y: 0 };
}

function rotate(shape: number[][]): number[][] {
  return shape[0].map((_, i) => shape.map((row) => row[i]).reverse());
}

function isValid(board: Board, piece: PieceState): boolean {
  for (let r = 0; r < piece.shape.length; r++) {
    for (let c = 0; c < piece.shape[r].length; c++) {
      if (!piece.shape[r][c]) continue;
      const nr = piece.y + r;
      const nc = piece.x + c;
      if (nr < 0 || nr >= ROWS || nc < 0 || nc >= COLS) return false;
      if (board[nr][nc]) return false;
    }
  }
  return true;
}

function placePiece(board: Board, piece: PieceState): Board {
  const next = board.map((row) => [...row]);
  for (let r = 0; r < piece.shape.length; r++) {
    for (let c = 0; c < piece.shape[r].length; c++) {
      if (piece.shape[r][c]) {
        next[piece.y + r][piece.x + c] = piece.type;
      }
    }
  }
  return next;
}

function clearLines(board: Board): { board: Board; cleared: number } {
  const remaining = board.filter((row) => row.some((cell) => !cell));
  const cleared = ROWS - remaining.length;
  const newRows = Array.from({ length: cleared }, () => Array(COLS).fill(null));
  return { board: [...newRows, ...remaining], cleared };
}

function drawBlock(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  color: string,
  size: number
) {
  ctx.fillStyle = color;
  ctx.fillRect(x * size + 1, y * size + 1, size - 2, size - 2);
  ctx.fillStyle = "rgba(255,255,255,0.15)";
  ctx.fillRect(x * size + 1, y * size + 1, size - 2, 4);
  ctx.fillStyle = "rgba(0,0,0,0.2)";
  ctx.fillRect(x * size + 1, y * size + size - 5, size - 2, 4);
}

export default function AidanTetris() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const nextCanvasRef = useRef<HTMLCanvasElement>(null);
  const boardRef = useRef<Board>(emptyBoard());
  const pieceRef = useRef<PieceState>(randomPiece());
  const nextPieceRef = useRef<PieceState>(randomPiece());
  const gameLoopRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isRunningRef = useRef(false);

  const [score, setScore] = useState(0);
  const [lines, setLines] = useState(0);
  const [level, setLevel] = useState(1);
  const [gameOver, setGameOver] = useState(false);
  const [started, setStarted] = useState(false);
  const [paused, setPaused] = useState(false);

  const scoreRef = useRef(0);
  const linesRef = useRef(0);
  const levelRef = useRef(1);
  const pausedRef = useRef(false);

  const renderBoard = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    ctx.fillStyle = "#0a1628";
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Grid lines
    ctx.strokeStyle = "rgba(34,211,238,0.05)";
    ctx.lineWidth = 0.5;
    for (let r = 0; r <= ROWS; r++) {
      ctx.beginPath();
      ctx.moveTo(0, r * BLOCK);
      ctx.lineTo(COLS * BLOCK, r * BLOCK);
      ctx.stroke();
    }
    for (let c = 0; c <= COLS; c++) {
      ctx.beginPath();
      ctx.moveTo(c * BLOCK, 0);
      ctx.lineTo(c * BLOCK, ROWS * BLOCK);
      ctx.stroke();
    }

    // Placed blocks
    const board = boardRef.current;
    for (let r = 0; r < ROWS; r++) {
      for (let c = 0; c < COLS; c++) {
        if (board[r][c]) {
          drawBlock(ctx, c, r, COLORS[board[r][c]!], BLOCK);
        }
      }
    }

    // Ghost piece
    const piece = pieceRef.current;
    let ghost = { ...piece, shape: piece.shape };
    while (isValid(board, { ...ghost, y: ghost.y + 1 })) ghost = { ...ghost, y: ghost.y + 1 };
    for (let r = 0; r < ghost.shape.length; r++) {
      for (let c = 0; c < ghost.shape[r].length; c++) {
        if (ghost.shape[r][c]) {
          ctx.fillStyle = `${COLORS[ghost.type]}30`;
          ctx.fillRect((ghost.x + c) * BLOCK + 1, (ghost.y + r) * BLOCK + 1, BLOCK - 2, BLOCK - 2);
          ctx.strokeStyle = `${COLORS[ghost.type]}60`;
          ctx.lineWidth = 1;
          ctx.strokeRect((ghost.x + c) * BLOCK + 1, (ghost.y + r) * BLOCK + 1, BLOCK - 2, BLOCK - 2);
        }
      }
    }

    // Active piece
    for (let r = 0; r < piece.shape.length; r++) {
      for (let c = 0; c < piece.shape[r].length; c++) {
        if (piece.shape[r][c]) {
          drawBlock(ctx, piece.x + c, piece.y + r, COLORS[piece.type], BLOCK);
        }
      }
    }
  }, []);

  const renderNext = useCallback(() => {
    const canvas = nextCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    ctx.fillStyle = "rgba(15,52,96,0.7)";
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    const np = nextPieceRef.current;
    const sh = np.shape;
    const offX = Math.floor((canvas.width / NEXT_BLOCK - sh[0].length) / 2);
    const offY = Math.floor((canvas.height / NEXT_BLOCK - sh.length) / 2);

    for (let r = 0; r < sh.length; r++) {
      for (let c = 0; c < sh[r].length; c++) {
        if (sh[r][c]) {
          drawBlock(ctx, offX + c, offY + r, COLORS[np.type], NEXT_BLOCK);
        }
      }
    }
  }, []);

  const spawnNext = useCallback(() => {
    const next = nextPieceRef.current;
    pieceRef.current = { ...next, x: Math.floor(COLS / 2) - Math.floor(next.shape[0].length / 2), y: 0 };
    nextPieceRef.current = randomPiece();
    if (!isValid(boardRef.current, pieceRef.current)) {
      isRunningRef.current = false;
      setGameOver(true);
    }
    renderNext();
  }, [renderNext]);

  const tick = useCallback(() => {
    if (!isRunningRef.current || pausedRef.current) return;
    const piece = pieceRef.current;
    const moved = { ...piece, y: piece.y + 1 };
    if (isValid(boardRef.current, moved)) {
      pieceRef.current = moved;
    } else {
      boardRef.current = placePiece(boardRef.current, piece);
      const { board, cleared } = clearLines(boardRef.current);
      boardRef.current = board;

      if (cleared > 0) {
        const points = [0, 100, 300, 500, 800][cleared] * levelRef.current;
        scoreRef.current += points;
        linesRef.current += cleared;
        const newLevel = Math.floor(linesRef.current / 10) + 1;
        levelRef.current = newLevel;
        setScore(scoreRef.current);
        setLines(linesRef.current);
        setLevel(newLevel);
      }
      spawnNext();
    }
    renderBoard();
    if (isRunningRef.current) {
      const speed = Math.max(100, 800 - (levelRef.current - 1) * 70);
      gameLoopRef.current = setTimeout(tick, speed);
    }
  }, [renderBoard, spawnNext]);

  const startGame = useCallback(() => {
    boardRef.current = emptyBoard();
    pieceRef.current = randomPiece();
    nextPieceRef.current = randomPiece();
    scoreRef.current = 0;
    linesRef.current = 0;
    levelRef.current = 1;
    setScore(0);
    setLines(0);
    setLevel(1);
    setGameOver(false);
    setStarted(true);
    setPaused(false);
    pausedRef.current = false;
    if (gameLoopRef.current) clearTimeout(gameLoopRef.current);
    isRunningRef.current = true;
    renderNext();
    renderBoard();
    gameLoopRef.current = setTimeout(tick, 800);
  }, [tick, renderBoard, renderNext]);

  const handlePause = useCallback(() => {
    if (!isRunningRef.current && !pausedRef.current) return;
    pausedRef.current = !pausedRef.current;
    setPaused(pausedRef.current);
    if (!pausedRef.current) {
      const speed = Math.max(100, 800 - (levelRef.current - 1) * 70);
      gameLoopRef.current = setTimeout(tick, speed);
    }
  }, [tick]);

  const move = useCallback((dir: "left" | "right" | "down" | "rotate" | "drop") => {
    if (!isRunningRef.current || pausedRef.current) return;
    const piece = pieceRef.current;
    let next: PieceState | null = null;

    if (dir === "left") next = { ...piece, x: piece.x - 1 };
    else if (dir === "right") next = { ...piece, x: piece.x + 1 };
    else if (dir === "down") next = { ...piece, y: piece.y + 1 };
    else if (dir === "rotate") next = { ...piece, shape: rotate(piece.shape) };
    else if (dir === "drop") {
      let dropped = piece;
      while (isValid(boardRef.current, { ...dropped, y: dropped.y + 1 })) {
        dropped = { ...dropped, y: dropped.y + 1 };
      }
      pieceRef.current = dropped;
      if (gameLoopRef.current) clearTimeout(gameLoopRef.current);
      tick();
      return;
    }

    if (next && isValid(boardRef.current, next)) {
      pieceRef.current = next;
      if (dir === "down") {
        if (gameLoopRef.current) clearTimeout(gameLoopRef.current);
        const speed = Math.max(100, 800 - (levelRef.current - 1) * 70);
        gameLoopRef.current = setTimeout(tick, speed);
      }
      renderBoard();
    }
  }, [tick, renderBoard]);

  useEffect(() => {
    renderBoard();
    renderNext();
  }, [renderBoard, renderNext]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!started) return;
      if (e.key === "ArrowLeft") { e.preventDefault(); move("left"); }
      else if (e.key === "ArrowRight") { e.preventDefault(); move("right"); }
      else if (e.key === "ArrowDown") { e.preventDefault(); move("down"); }
      else if (e.key === "ArrowUp") { e.preventDefault(); move("rotate"); }
      else if (e.key === " ") { e.preventDefault(); move("drop"); }
      else if (e.key === "p" || e.key === "P") handlePause();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [move, handlePause, started]);

  useEffect(() => {
    return () => {
      if (gameLoopRef.current) clearTimeout(gameLoopRef.current);
      isRunningRef.current = false;
    };
  }, []);

  return (
    <section id="tetris" className="py-20 px-6 relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#0d0d1a] to-transparent pointer-events-none" />
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#e879f9]/30 to-transparent" />

      <div className="max-w-5xl mx-auto relative z-10">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[#e879f9]/30 bg-[#e879f9]/10 mb-4">
            <i className="ri-gamepad-fill text-[#e879f9] text-sm" />
            <span className="font-mono text-xs text-[#e879f9] uppercase tracking-widest">Mini Game</span>
          </div>
          <h2 className="font-orbitron text-4xl md:text-5xl font-black uppercase text-white mb-3">
            AIDAN <span style={{ color: "#4ecca3", textShadow: "0 0 20px #4ecca3" }}>TETRIS</span>
          </h2>
          <p className="text-slate-400 font-rajdhani text-lg">Classic puzzle game reimagined. Can you beat my high score?</p>
        </div>

        {/* Game area */}
        <div className="flex flex-col lg:flex-row items-center lg:items-start justify-center gap-6">
          {/* Main canvas */}
          <div className="relative">
            <div className="absolute -inset-1 rounded-lg bg-gradient-to-b from-[#4ecca3]/30 to-[#22d3ee]/10 blur-sm" />
            <canvas
              ref={canvasRef}
              width={COLS * BLOCK}
              height={ROWS * BLOCK}
              className="relative block rounded-lg"
              style={{ imageRendering: "pixelated" }}
            />
          </div>

          {/* Side panel */}
          <div className="flex flex-row lg:flex-col gap-4 w-full lg:w-40">
            {/* Stats */}
            <div className="flex-1 lg:flex-none rounded-xl border border-[#4ecca3]/20 bg-black/40 backdrop-blur p-4">
              <div className="font-orbitron text-xs text-[#4ecca3] text-center mb-3 tracking-widest uppercase">Stats</div>
              <div className="space-y-2 text-center font-rajdhani">
                <div className="text-slate-400 text-sm">Score</div>
                <div className="font-orbitron text-xl font-bold" style={{ color: "#4ecca3" }}>{score.toLocaleString()}</div>
                <div className="text-slate-400 text-sm mt-2">Lines</div>
                <div className="font-orbitron text-xl font-bold text-white">{lines}</div>
                <div className="text-slate-400 text-sm mt-2">Level</div>
                <div className="font-orbitron text-xl font-bold text-[#e879f9]">{level}</div>
              </div>
            </div>

            {/* Next piece */}
            <div className="flex-1 lg:flex-none rounded-xl border border-[#4ecca3]/20 bg-black/40 backdrop-blur p-4">
              <div className="font-orbitron text-xs text-[#4ecca3] text-center mb-3 tracking-widest uppercase">Next</div>
              <div className="flex justify-center items-center h-[96px]">
                <canvas
                  ref={nextCanvasRef}
                  width={120}
                  height={96}
                  className="rounded-lg"
                  style={{ imageRendering: "pixelated" }}
                />
              </div>
            </div>

            {/* Controls */}
            <div className="flex-1 lg:flex-none rounded-xl border border-[#4ecca3]/20 bg-black/40 backdrop-blur p-4">
              <div className="font-orbitron text-xs text-[#4ecca3] text-center mb-3 tracking-widest uppercase">Controls</div>
              <div className="space-y-1.5 font-rajdhani text-xs text-slate-400">
                {[
                  { action: "Move", key: "← →" },
                  { action: "Rotate", key: "↑" },
                  { action: "Soft Drop", key: "↓" },
                  { action: "Hard Drop", key: "SPACE" },
                  { action: "Pause", key: "P" },
                ].map(({ action, key }) => (
                  <div key={action} className="flex justify-between items-center">
                    <span>{action}</span>
                    <span className="font-mono text-[10px] bg-[#4ecca3]/10 text-[#4ecca3] px-1.5 py-0.5 rounded">{key}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Buttons */}
            <div className="flex flex-col gap-2 lg:flex-col">
              <button
                onClick={startGame}
                className="font-orbitron text-xs uppercase tracking-wider px-4 py-2.5 rounded-lg cursor-pointer whitespace-nowrap transition-all duration-200 bg-[#4ecca3] text-black font-bold hover:bg-[#3da58a]"
              >
                {started ? "Restart" : "Start"}
              </button>
              {started && !gameOver && (
                <button
                  onClick={handlePause}
                  className="font-orbitron text-xs uppercase tracking-wider px-4 py-2.5 rounded-lg cursor-pointer whitespace-nowrap transition-all duration-200 border border-[#e879f9]/50 text-[#e879f9] bg-transparent hover:bg-[#e879f9]/10"
                >
                  {paused ? "Resume" : "Pause"}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Mobile touch controls */}
        <div className="flex justify-center gap-4 mt-6 lg:hidden">
          <button onClick={() => move("left")} className="w-14 h-14 flex items-center justify-center rounded-xl border border-[#4ecca3]/30 bg-[#4ecca3]/10 text-[#4ecca3] text-xl cursor-pointer active:bg-[#4ecca3]/30 transition-all">
            <i className="ri-arrow-left-s-line" />
          </button>
          <div className="flex flex-col gap-2">
            <button onClick={() => move("rotate")} className="w-14 h-14 flex items-center justify-center rounded-xl border border-[#e879f9]/30 bg-[#e879f9]/10 text-[#e879f9] text-xl cursor-pointer active:bg-[#e879f9]/30 transition-all">
              <i className="ri-refresh-line" />
            </button>
            <button onClick={() => move("down")} className="w-14 h-14 flex items-center justify-center rounded-xl border border-[#4ecca3]/30 bg-[#4ecca3]/10 text-[#4ecca3] text-xl cursor-pointer active:bg-[#4ecca3]/30 transition-all">
              <i className="ri-arrow-down-s-line" />
            </button>
          </div>
          <button onClick={() => move("right")} className="w-14 h-14 flex items-center justify-center rounded-xl border border-[#4ecca3]/30 bg-[#4ecca3]/10 text-[#4ecca3] text-xl cursor-pointer active:bg-[#4ecca3]/30 transition-all">
            <i className="ri-arrow-right-s-line" />
          </button>
          <button onClick={() => move("drop")} className="w-14 h-14 flex items-center justify-center rounded-xl border border-[#facc15]/30 bg-[#facc15]/10 text-[#facc15] text-xl cursor-pointer active:bg-[#facc15]/30 transition-all">
            <i className="ri-arrow-down-double-line" />
          </button>
        </div>
      </div>

      {/* Game Over overlay */}
      {gameOver && (
        <div className="absolute inset-0 flex items-center justify-center z-20 bg-black/60 backdrop-blur-sm">
          <div className="text-center p-10 rounded-2xl border-2 border-[#4ecca3] bg-[#07070f]/90 shadow-[0_0_40px_rgba(78,204,163,0.4)] max-w-sm w-full mx-4">
            <div className="font-orbitron text-3xl font-black text-[#4ecca3] mb-2 uppercase">Game Over</div>
            <p className="text-slate-400 font-rajdhani mb-6">Better luck next time, soldier.</p>
            <div className="mb-2 text-slate-400 font-rajdhani text-sm">Final Score</div>
            <div className="font-orbitron text-4xl font-bold text-white mb-1">{score.toLocaleString()}</div>
            <div className="text-slate-500 font-rajdhani text-sm mb-6">{lines} lines &bull; Level {level}</div>
            <button
              onClick={startGame}
              className="font-orbitron text-sm uppercase tracking-wider px-8 py-3 rounded-full bg-[#4ecca3] text-black font-bold cursor-pointer hover:bg-[#3da58a] transition-all duration-200 whitespace-nowrap"
            >
              Play Again
            </button>
          </div>
        </div>
      )}

      {/* Paused overlay */}
      {paused && !gameOver && (
        <div className="absolute inset-0 flex items-center justify-center z-20 bg-black/50 backdrop-blur-sm">
          <div className="text-center">
            <div className="font-orbitron text-4xl font-black text-[#e879f9] uppercase animate-pulse" style={{ textShadow: "0 0 30px #e879f9" }}>
              PAUSED
            </div>
            <p className="text-slate-400 font-rajdhani mt-2">Press P to resume</p>
          </div>
        </div>
      )}
    </section>
  );
}
